import { useEffect, useState } from "react";
import Input from "../common/Input";
import Button from "../common/Button";
import CustomSelect from "../common/CustomSelect";
import toast from "react-hot-toast";
import { addEducation } from "../../../services/educationServices";
import { getAllDegrees } from "../../../services/degreeServices";

import { useNavigate } from "react-router";

const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const activeOptions = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];

const AddEducationForm = () => {
  const navigate = useNavigate();
  const [degree, setDegree] = useState(null);
  const [degreeOptions, setDegreeOptions] = useState([]);
  const [course, setCourse] = useState("");
  const [university, setUniversity] = useState("");
  const [place, setPlace] = useState("");
  const [website, setWebsite] = useState("");
  const [isActive, setIsActive] = useState(activeOptions[0]);
  const [status, setStatus] = useState(statusOptions[1]);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchDegrees = async () => {
      try {
        const response = await getAllDegrees();
        const degreeOptions =
          response?.data?.map((degreeItem) => ({
            value: degreeItem.value,
            label: degreeItem.label,
          })) || [];

        setDegreeOptions(degreeOptions);
      } catch (error) {
        console.error("Failed to fetch degrees:", error);
        toast.error("Failed to load degree options");
      }
    };

    fetchDegrees();
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!degree) newErrors.degree = "Degree is required";
    if (!course.trim()) newErrors.course = "Course is required";
    if (!university.trim()) newErrors.university = "University is required";
    if (!place.trim()) newErrors.place = "Place is required";
    if (!website.trim()) {
      newErrors.website = "Website is required";
    } else {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(website)) {
        newErrors.website =
          "Please enter a valid URL (must start with http:// or https://)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      degree: degree.value,
      course: course.trim(),
      university: university.trim(),
      place: place.trim(),
      website: website.trim(),
      isActive: isActive ? isActive.value : true,
      status: status ? status.value : "pending",
    };

    try {
      setLoading(true);
      await addEducation(payload);
      toast.success("Education record created successfully!");

      setDegree(null);
      setCourse("");
      setUniversity("");
      setPlace("");
      setWebsite("");
      setIsActive(activeOptions[0]);
      setStatus(statusOptions[0]);
      setErrors({});

      navigate("/admin/education/list");
    } catch (error) {
      toast.error(error.message || "Failed to create education record");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value, setter) => {
    setter(value);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="bg-[#F5F0FF] p-6 rounded-lg font-sans space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <CustomSelect
            label="Degree"
            value={degree}
            onChange={(val) => handleInputChange("degree", val, setDegree)}
            options={degreeOptions}
            placeholder="Select Degree"
            required
          />
          {errors.degree && (
            <p className="text-red font-semibold text-xs mt-1">
              {errors.degree}
            </p>
          )}
        </div>

        <div>
          <Input
            label="Course"
            value={course}
            onChange={(val) => handleInputChange("course", val, setCourse)}
            placeholder="e.g., Computer Science, Business Administration"
            required
          />
          {errors.course && (
            <p className="text-red font-semibold text-xs mt-1">
              {errors.course}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="University"
            value={university}
            onChange={(val) =>
              handleInputChange("university", val, setUniversity)
            }
            placeholder="e.g., Harvard University, MIT"
            required
          />
          {errors.university && (
            <p className="text-red font-semibold text-xs mt-1">
              {errors.university}
            </p>
          )}
        </div>

        <div>
          <Input
            label="Place"
            value={place}
            onChange={(val) => handleInputChange("place", val, setPlace)}
            placeholder="e.g., Cambridge, MA, USA"
            required
          />
          {errors.place && (
            <p className="text-red font-semibold text-xs mt-1">
              {errors.place}
            </p>
          )}
        </div>
      </div>

      <div>
        <Input
          label="Website"
          value={website}
          onChange={(val) => handleInputChange("website", val, setWebsite)}
          placeholder="https://university.edu"
          required
        />
        {errors.website && (
          <p className="text-red font-semibold text-xs mt-1">
            {errors.website}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <CustomSelect
            label="Status"
            value={status}
            onChange={(val) => handleInputChange("status", val, setStatus)}
            options={statusOptions}
            placeholder="Select Status"
          />
        </div>

        <div>
          <CustomSelect
            label="Active Status"
            value={isActive}
            onChange={(val) => handleInputChange("isActive", val, setIsActive)}
            options={activeOptions}
            placeholder="Select Active Status"
          />
        </div>
      </div>

      <div className="pt-4 space-y-2">
        <Button
          className="w-full"
          type="button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Add Education Record"}
        </Button>
        <Button
          variant="text"
          className="w-full"
          onClick={() => navigate("/admin/education/list")}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AddEducationForm;
